import { useContext, useEffect, useState, useRef } from "react";
import { AuthContext } from "../../../../contexts/user-context/user-context";
import { Currencies, CURRENCY, displayMoney, get_symbol } from "../../../../utils/money";
import { API_ENDPOINT } from "../../../../configurations/global.config";
import axios from "axios";
import { usePaginatedData } from "../../../../hooks/pagination";
import { BalanceHistoryPeriod, IBalance, ILedgerEntry } from "../../../../interfaces/common/wallet";
import { useWallet } from "../../../../hooks/wallet";
import { BranchType } from "../../../../interfaces/delivery-company/branches";
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Divider,
  Grid,
} from "@mui/material";
import { useSnackBar } from "../../../../contexts/snackbar";
import { FaWarehouse } from "react-icons/fa";
import { OrgSettingsContext } from "../../../../contexts/org-settings-context";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { BalanceHistoryChart } from "./history-graph";
import Grid2 from "@mui/material/Unstable_Grid2";
import { BranchSelector } from "../../../common/selectors/branch-selector";


// ------------------ Wallet Card (Credit Card Style) ------------------
interface WalletCardProps {
  balance: IBalance;
  loading: boolean;
}

const WalletCard = ({ balance, loading }: WalletCardProps) => {
  return (
    <Card
      sx={{
        width: 360,
        maxWidth: "95%",
        borderRadius: 4,
        background: "linear-gradient(135deg, #5A189A 0%, #240046 100%)",
        color: "white",
        boxShadow: "0px 10px 25px rgba(0,0,0,0.2)",
        p: 3,
        mx: "auto",
        mt: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
          Current Balance
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
          {loading ? (
            <CircularProgress size={32} color="inherit" />
          ) : (
            displayMoney(balance.current.balance)
          )}
        </Typography>
      </Box>
    </Card>
  );
};

// ------------------ Transactions Table (Infinite Scroll) ------------------
interface TransactionsTableProps {
  transactions: ILedgerEntry[];
  loading: boolean;
  onScrollEnd: () => void;
  hasMore: boolean;
}

const TransactionsTable = ({
    transactions,
    loading,
    onScrollEnd,
    hasMore,
  }: TransactionsTableProps) => {

    const [selectedPeriod, setSelectedPeriod] = useState<BalanceHistoryPeriod>(BalanceHistoryPeriod.LAST_WEEK);
  
    // Scroll event
    useEffect(() => {
        const handleScroll = () => {
          if (loading || !hasMore) return;
      
          const scrollTop = window.scrollY;
          const windowHeight = window.innerHeight;
          const docHeight = document.documentElement.scrollHeight;
      
          // Trigger load when near bottom of the page (e.g., 100px)
          if (scrollTop + windowHeight >= docHeight - 100) {
            onScrollEnd();
          }
        };
      
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
      }, [loading, hasMore, onScrollEnd]);

  
    if (!transactions.length && !loading) {
      return (
        <Typography variant="body2" sx={{ mt: 3 }}>
          No transactions found.
        </Typography>
      );
    }
  
    return (
      <Box
        sx={{
          mt: 3,
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#ccc", borderRadius: 4 },
        }}
      >
        <Table size="small" stickyHeader >
            <TableHead>
            <TableRow>
                <TableCell><b>Amount</b></TableCell>
                <TableCell><b>Description</b></TableCell>
                <TableCell><b>Performed By</b></TableCell>
                <TableCell><b>Date</b></TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
            {transactions.map((t) => (
                <TableRow key={t.txid} hover>
                <TableCell>{t.tx_type.toLowerCase() === "credit" ? (
                        <Box sx={{ display: "flex", alignItems: "center",  }}>
                        <ArrowUpward fontSize="small" sx={{ mr: 0.5 }} color={"success"}/>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{t.amount} {get_symbol(t.currency)}</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center",}}>
                        <ArrowDownward fontSize="small" sx={{ mr: 0.5 }} color={"error"} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{t.amount} {get_symbol(t.currency)}</Typography>
                        </Box>
                    )} </TableCell>
                <TableCell>{t.description}</TableCell>
                <TableCell>{t.performed_by || "-"}</TableCell>
                <TableCell>{new Date(t.tx_time).toLocaleString()}</TableCell>
                </TableRow>
            ))}
            </TableBody>
        </Table>

        {loading && (
            <Box sx={{ textAlign: "center", py: 2 }}>
            <CircularProgress size={24} />
            </Box>
        )}

        {!hasMore && !loading && (
            <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", py: 1, opacity: 0.6 }}
            >
            End of transactions
            </Typography>
        )}
      </Box>
    );
  };
// ------------------ Main Wallet Component ------------------
export const Wallet = () => {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const { getBranchBalance, getBranchLedger } = useWallet();
  const [branches, setBranches] = useState<BranchType[]>([]);
  const [branch, setBranch] = useState<BranchType | null>(null);
  const [branchBalance, setBranchBalance] = useState<IBalance | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState<BalanceHistoryPeriod>(BalanceHistoryPeriod.LAST_MONTH);

  const {
    data: transactions,
    loading,
    hasMore,
    loadNextPage,
  } = usePaginatedData<ILedgerEntry>(
    async (page: number, pageSize: number) =>
      branch
        ? await getBranchLedger(branch.id, page, pageSize)
        : { count: 0, results: [], next: null, previous: null },
    [branch],
    true // Enable infinite scroll append mode
  );

  

  // Fetch branch balance
  useEffect(() => {
    setLoadingBalance(true);
    if (branch) {
      getBranchBalance(branch.id, {period: historyPeriod})
        .then(setBranchBalance)
        .catch(() => showMessage("Failed to fetch balance", "error"))
        .finally(()=> setLoadingBalance(false));
    } else {
      setBranchBalance(null);
      setLoadingBalance(false);
    }
  }, [branch, historyPeriod]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Branch Selector */}
      <BranchSelector
        selectedBranch={branch}
        onSelect={setBranch}
      />

      {/* Wallet Card */}
      {branchBalance ? (
        <Grid container spacing={2} >
            <Grid xs={12} md={6}><Box height={"100%"} display={"flex"} width={"100%"} alignItems={"center"} justifyContent={"center"}><WalletCard balance={branchBalance} loading={loadingBalance} /></Box></Grid>
            <Grid xs={12} md={6}><BalanceHistoryChart period={historyPeriod} historyData={branchBalance.history} onPeriodSelected={setHistoryPeriod} loading={loadingBalance}/></Grid>
        </Grid>
      ) : (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Transactions */}
      {branch && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6">Transaction History</Typography>
          <TransactionsTable
            transactions={transactions}
            loading={loading}
            hasMore={hasMore}
            onScrollEnd={loadNextPage}
          />
        </>
      )}
    </Box>
  );
};
